// ─── « Repas » (compositions) — étage entre Recette et Menu ───────────
// Un Repas = composition nommée : PLUSIEURS recettes + des « extras » (hors
// recette : fruits, pain, yaourts…), pour remplir correctement le panier de
// courses (liste de courses = incrément suivant). Collection plate
// `repasComposes`, indexée par espaceId. Porté de MoPCal-v3
// services/repasComposes.js.
//   { espaceId, nom, categorie?, icone?, personnes,
//     recettes: [{ recetteId, personnes }],
//     extras:   [{ nom, quantite, unite, prix }],
//     creePar, creeParNom, creeLe, maj }
import { db } from '@mop/core';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { coutRecette } from './recettes';
import { personnesRecette } from './personnes';

export function ecouterRepasComposes(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'repasComposes'), where('espaceId', '==', espaceId));
  return onSnapshot(q, (snap) => {
    const liste = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    liste.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
    callback(liste);
  }, (e) => { callback([]); onErreur?.(e); });
}

// Firestore rejette undefined → on nettoie avant écriture.
function nettoyer(r) {
  return {
    nom:       (r.nom || '').trim(),
    categorie: r.categorie || '',
    icone:     r.icone || '',
    // `personnes` du repas = pour combien de convives la composition est
    // prévue. `personnes` d'une recette dans `recettes[]` = combien de
    // convives CETTE recette sert ici (cf. services/personnes.js).
    personnes: Math.max(1, Number(r.personnes) || 4),
    recettes:  (r.recettes || []).filter(x => x && x.recetteId)
      .map(x => ({ recetteId: x.recetteId, personnes: Math.max(1, Number(x.personnes) || Number(r.personnes) || 4) })),
    extras:    (r.extras || [])
      .filter(i => (i.nom || '').trim())
      .map(i => ({
        nom:      (i.nom || '').trim(),
        quantite: (i.quantite != null ? String(i.quantite) : '').trim(),
        unite:    (i.unite || '').trim(),
        prix:     (i.prix != null && i.prix !== '') ? (Number(i.prix) || 0) : null,
      })),
  };
}

export async function creerRepas({ espaceId, creePar, creeParNom, ...r }) {
  if (!espaceId) throw new Error('Aucun espace actif');
  const data = nettoyer(r);
  if (!data.nom) throw new Error('Donne un nom au repas.');
  const ref = await addDoc(collection(db, 'repasComposes'), {
    espaceId, ...data,
    creePar: creePar || null, creeParNom: creeParNom || '',
    creeLe: new Date().toISOString(), maj: new Date().toISOString(),
  });
  return ref.id;
}

export async function modifierRepas(id, r) {
  await updateDoc(doc(db, 'repasComposes', id), { ...nettoyer(r), maj: new Date().toISOString() });
}

export async function supprimerRepas(id) {
  await deleteDoc(doc(db, 'repasComposes', id));
}

export async function dupliquerRepas(repas) {
  const { id, creeLe, maj, ...r } = repas;
  return creerRepas({ ...r, nom: `${r.nom} (copie)` });
}

// Coût estimé d'un repas = coût des recettes, mis à l'échelle du nombre de
// personnes qu'elles servent dans CE repas, + les extras.
export function coutRepas(repas, recettesById) {
  let c = 0;
  for (const x of (repas?.recettes || [])) {
    const r = recettesById?.[x.recetteId];
    if (!r) continue;
    const R  = Math.max(1, Number(x.personnes) || Number(repas?.personnes) || 1);
    const Rp = personnesRecette(r);
    c += coutRecette(r) * (R / Rp);
  }
  for (const e of (repas?.extras || [])) c += Number(e.prix) || 0;
  return Math.round(c * 100) / 100;
}

// Sous-titre récap : « 2 recettes · 3 extras ».
export function resumeRepas(repas) {
  const nr = (repas?.recettes || []).length;
  const ne = (repas?.extras || []).length;
  const parts = [];
  if (nr) parts.push(`${nr} recette${nr > 1 ? 's' : ''}`);
  if (ne) parts.push(`${ne} extra${ne > 1 ? 's' : ''}`);
  return parts.join(' · ') || 'Vide';
}
